import { useCallback, useEffect, useRef, useState } from 'react';
import { api, connectWallet, currentWallet, errorText, injected } from './walletClient';

export function usePolygonWallet() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [matches, setMatches] = useState(false);
  const connecting = useRef(false), sequence = useRef(0);
  const refresh = useCallback(async () => {
    const revision = ++sequence.current;
    const response = await api.get('/state');
    if (revision !== sequence.current) return;
    setData(response.data);
    let valid = false;
    if (response.data.address) {
      try { await currentWallet(response.data.address); valid = true; } catch (_) { /* Explicit disconnected/wrong-chain state. */ }
    }
    if (revision === sequence.current) setMatches(valid);
    return response.data;
  }, []);
  const run = async (name, action) => {
    setBusy(name); setError('');
    try { const result = await action(); await refresh(); return result; }
    catch (exception) { setError(errorText(exception)); try { await refresh(); } catch (_) { /* Keep last state visibly unavailable. */ } }
    finally { setBusy(''); }
  };
  useEffect(() => {
    refresh().catch(exception => setError(errorText(exception)));
    const provider = injected();
    const changed = () => {
      ++sequence.current; setMatches(false);
      if (connecting.current) return;
      setData(null);
      api.post('/disconnect').then(refresh).catch(exception => setError(errorText(exception)));
    };
    provider?.on?.('accountsChanged', changed); provider?.on?.('chainChanged', changed);
    return () => { ++sequence.current; provider?.removeListener?.('accountsChanged', changed); provider?.removeListener?.('chainChanged', changed); };
  }, [refresh]);
  const connect = () => run('connect', async () => {
    connecting.current = true;
    try { await connectWallet(data.config); } finally { connecting.current = false; }
  });
  const disconnect = () => run('disconnect', async () => { ++sequence.current; setMatches(false); await api.post('/disconnect'); });
  return { data, busy, error, matches, run, refresh, connect, disconnect,
    canSign: !!(data?.config.enabled && matches && data?.verified_until * 1000 > Date.now() && !data?.paused && Number(data?.config.max_trade_usd) > 0 && Number(data?.config.max_day_usd) > 0 && Number(data?.reserved_today_usd) <= Number(data?.config.max_day_usd)) };
}